import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Box, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PreviewIcon from "@mui/icons-material/Preview";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PropTypes from "prop-types";

import AuditSchedularApproval from "./AuditSchedularApproval";
import Tooltip from "Components/Common/Tooltip";
import Modal from "Components/Common/Modal";
import RegisterBuilding from "pages/FireSafety/RegisterBuilding";
import { fireSafetyApplicationStatusTypes } from "common/constant";

import { ApplicationIcon } from "./style";
import FireSafetyViewObservationNotice from "./FireSafetyViewObservationNotice";
import {
  useLazyBuildingScheduleQuery,
  useLazyGetFireSafetyObservationNotice,
} from "apis/FireSafety/FireSafetyObservation";
import { getImageUrl } from "utils";
import NoticePreviewModal from "Components/Common/ViewApplicationDetailComponents/NoticePreviewModal";
import NoticeActionButtons from "./NoticeActionButtons";
import Backdrop from "Components/Common/Backdrop";

const FireSafetyApplicationConsumerAction = ({ t, item }) => {
  const {
    id,
    isChangedByUser,
    developerBuilding,
    isScheduleAdded,
    isAcknowledged,
    scheduleId,
    buildingInformationStatus: { id: applicatioNStatusId },
  } = item;

  const [editApplicationId, setEditApplicationId] = useState("");
  const [isViewNoticeModalOpen, setIsViewNoticeModalOpen] = useState(false);
  const [applicationIdForAuditSchedule, setApplicationIdForAuditSchedule] =
    useState("");
  const [noticePreview, setNoticePreview] = useState(null);

  const [
    getNotice,
    { data: { data: noticeData } = {}, isLoading: isNoticeLoading },
  ] = useLazyGetFireSafetyObservationNotice();

  const [
    getBuildingScheduleById,
    {
      isLoading: isScheduleDataLoading,
      isFetching: isScheduleDataFetching,
      data: { data: buildingSchedulesData } = [],
    },
  ] = useLazyBuildingScheduleQuery();

  const [searchParams, setSearchParams] = useSearchParams();
  const applicationIdForAuditSchedular = +searchParams.get("auditScheduleId");

  const isAuditScheduleIconVisible = useMemo(
    () =>
      applicatioNStatusId === fireSafetyApplicationStatusTypes["Approved"] &&
      !isAcknowledged &&
      !!isScheduleAdded &&
      !isChangedByUser,
    [applicatioNStatusId, isAcknowledged, isScheduleAdded, isChangedByUser]
  );

  const handleViewNotice = async () => {
    let pdfUrl = "";
    try {
      const scheduleData = await getBuildingScheduleById(id);
      const scheduleID = scheduleData?.data?.[0]?.scheduleId;

      if (scheduleID) {
        const res = await getNotice(scheduleID);
        pdfUrl = res?.data?.url;
      }
      setNoticePreview({
        url: pdfUrl ? getImageUrl(pdfUrl) : null,
        documentTypeName: "Generated Notice",
        documentType: "pdf",
        documentName: "GeneratedNotice",
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (
      applicationIdForAuditSchedular === developerBuilding?.id &&
      !applicationIdForAuditSchedule &&
      isAuditScheduleIconVisible
    ) {
      setApplicationIdForAuditSchedule(id);
    }
    if (
      !isAuditScheduleIconVisible &&
      developerBuilding?.id === applicationIdForAuditSchedular
    ) {
      searchParams.delete("auditScheduleId");
      setSearchParams(searchParams);
    }
  }, [
    isAuditScheduleIconVisible,
    applicationIdForAuditSchedular,
    developerBuilding?.id,
    applicationIdForAuditSchedule,
  ]);
  
  if (isScheduleDataLoading || isScheduleDataFetching || isNoticeLoading) {
    return <Backdrop open />;
  }

  return (
    <>
      {!!editApplicationId && (
        <Modal
          isOpen={!!editApplicationId}
          handleClose={() => setEditApplicationId("")}
          maxWidth="lg"
          title="Edit Register Building"
        >
          <RegisterBuilding
            applicationId={editApplicationId}
            handleClose={() => setEditApplicationId("")}
          />
        </Modal>
      )}

      {/* Audit Schedule Approval */}
      {developerBuilding?.id === applicationIdForAuditSchedular &&
        !!applicationIdForAuditSchedule && (
          <AuditSchedularApproval
            developerBuilding={developerBuilding}
            applicationId={applicationIdForAuditSchedule}
            handleClose={() => {
              setApplicationIdForAuditSchedule("");
              searchParams.delete("auditScheduleId");
              setSearchParams(searchParams);
            }}
            buildingName={developerBuilding?.name ?? ""}
          />
        )}

      {/* Observation notice document */}
      {isViewNoticeModalOpen && (
        <FireSafetyViewObservationNotice
          applicationId={id}
          handleClose={() => setIsViewNoticeModalOpen(false)}
          buildingName={developerBuilding?.name ?? ""}
        />
      )}

      {!!noticePreview && (
        <NoticePreviewModal
          isOpen
          handleClose={() => {
            setNoticePreview(null);
          }}
          noticeData={noticeData}
          actionButton={
            <NoticeActionButtons
              noticeData={noticeData}
              setNoticePreview={setNoticePreview}
            />
          }
          {...noticePreview}
        />
      )}

      <Box width="150px">
        {/* View */}
        <IconButton
          onClick={() => {
            searchParams.set("applicationId", id);
            setSearchParams(searchParams);
          }}
        >
          <Tooltip title={t("View Application Form")}>
            <RemoveRedEyeIcon
              fontSize="small"
              color="primary"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>

        {/* Edit */}
        <IconButton
          onClick={() => {
            setEditApplicationId(id);
          }}
        >
          <Tooltip title={t("Edit Application Form")}>
            <EditIcon
              color="primary"
              fontSize="small"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>

        {/* Audit Schedule */}
        {isAuditScheduleIconVisible && (
          <IconButton
            onClick={() => {
              setApplicationIdForAuditSchedule(id);
              searchParams.set("auditScheduleId", developerBuilding.id);
              setSearchParams(searchParams);
            }}
          >
            <Tooltip title={t("Audit Schedule")}>
              <CalendarMonthIcon
                color="primary"
                fontSize="small"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        )}

        {/* View Building Observation and upload correction images */}
        <IconButton
          onClick={() => {
            searchParams.set("buildingObservationId", id);
            setSearchParams(searchParams);
          }}
        >
          <Tooltip title={t("View Building Observation")}>
            <ApplicationIcon
              color="primary"
              fontSize="small"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>

        <IconButton
          onClick={() => {
            // setIsViewNoticeModalOpen(true);
            handleViewNotice();
          }}
        >
          <Tooltip title={t("View Notice")}>
            <PreviewIcon
              fontSize="small"
              color="primary"
              className="cursor-pointer"
            />
          </Tooltip>
        </IconButton>
      </Box>
    </>
  );
};

FireSafetyApplicationConsumerAction.propTypes = {
  t: PropTypes.func,
  item: PropTypes.object,
};

export default withTranslation()(FireSafetyApplicationConsumerAction);
